/**
 * Deterministic combat simulation (docs/03, SCOPE_STAGE1).
 * Pure function of (seed, setupA, setupB, config) — no DOM, no Math.random, no floats.
 * simulate() twice with identical inputs MUST return deepEqual results.
 */
import { createRng, type Rng } from './rng'
import { hexDistance, hexIndex, type HexPos } from './hex'
import { findPath } from './pathfinding'
import { applyPctBonus, attackIntervalTicks, effectiveCrit, mitigate, weaponBaseDamage } from './damage'
import type { BalanceConfig, ChampionConfig, WeaponConfig } from '../config/balance'
import type {
  CombatResult, DamageType, DotKind, EndReason, SimEvent, TeamId, TeamSetup,
  UnitMeta, UnitSnapshot, UnitState,
} from './types'

interface ResolvedWeapon {
  key: string
  cfg: WeaponConfig
  tier: number
  base: number // effective base damage (weapon tier applied)
  intervalTicks: number
  range: number
  damagePct: number // affinity bonus
  cooldown: number
}

interface Dot {
  kind: DotKind
  intervalsLeft: number
  ticksToNext: number
}

interface SimUnit {
  id: number
  team: TeamId
  champ: string
  c: ChampionConfig
  weapons: ResolvedWeapon[]
  maxHp: number
  hp: number
  shield: number
  mana: number
  hex: HexPos
  moveFrom: HexPos | null
  moveTicksTotal: number
  moveTicksLeft: number
  state: UnitState
  targetId: number
  tauntedBy: number
  tauntTicksLeft: number
  dots: Dot[]
  guaranteedCrit: boolean
  attackRange: number
  displayTier: number // display-only, no combat effect
}

interface SimState {
  cfg: BalanceConfig
  rng: Rng
  cols: number
  rows: number
  units: SimUnit[]
  occ: Int32Array // hexIndex -> unit id or -1
  events: SimEvent[]
  tick: number
}

function affinityMatches(c: ChampionConfig, w: WeaponConfig): boolean {
  if (!c.affinity) return false
  const m = c.affinity.match
  if (m === 'class:two') return w.hands === 2
  if (m.startsWith('tag:')) return w.tag === m.slice(4)
  return false
}

function resolveWeapons(
  c: ChampionConfig, keys: string[], cfg: BalanceConfig,
  extraAsPct: number, tiers: number[] | undefined,
): ResolvedWeapon[] {
  const effective = keys.length > 0 ? keys : ['fist']
  const out: ResolvedWeapon[] = []
  let hands = 0
  for (let i = 0; i < effective.length; i++) {
    const key = effective[i]!
    const w = cfg.weapons[key]
    if (!w) throw new Error(`unknown weapon: ${key}`)
    if (hands + w.hands > 2) throw new Error(`hand slots exceeded (max 2): ${keys.join(',')}`)
    hands += w.hands
    const match = affinityMatches(c, w)
    const asBonus = (match ? c.affinity!.attackSpeedPct : 0) + extraAsPct
    const interval = attackIntervalTicks(c.attackBaseTicks, w.speedPct, asBonus)
    // weapon tier scales base damage (Stage 2, A6): base * (mult/100)^(tier-1), integer
    const tier = Math.max(1, tiers?.[i] ?? 1)
    let base = w.base
    for (let t = 1; t < tier; t++) base = Math.floor((base * cfg.upgrade.weaponTierDmgMultPct) / 100)
    out.push({
      key,
      cfg: w,
      tier,
      base,
      intervalTicks: interval,
      range: w.range + (match ? c.affinity!.rangeBonus : 0),
      damagePct: match ? c.affinity!.damagePct : 0,
      cooldown: 0,
    })
  }
  return out
}

function buildUnits(setupA: TeamSetup, setupB: TeamSetup, cfg: BalanceConfig): SimUnit[] {
  if (setupA.length === 0 || setupB.length === 0) throw new Error('both teams need at least 1 unit')
  const rows = cfg.grid.rowsPerSide * 2
  // Fixed processing order: interleave A/B by deployment index (reduces first-mover bias,
  // still 100% deterministic).
  const interleaved: Array<{ team: TeamId; s: TeamSetup[number] }> = []
  const maxLen = Math.max(setupA.length, setupB.length)
  for (let i = 0; i < maxLen; i++) {
    const a = setupA[i]
    const b = setupB[i]
    if (a) interleaved.push({ team: 'A', s: a })
    if (b) interleaved.push({ team: 'B', s: b })
  }
  return interleaved.map(({ team, s }, id) => {
    const c = cfg.champions[s.champ]
    if (!c) throw new Error(`unknown champion: ${s.champ}`)
    const half = cfg.grid.rowsPerSide
    const validRow = team === 'A' ? s.hex.row >= half && s.hex.row < rows : s.hex.row >= 0 && s.hex.row < half
    if (!validRow || s.hex.col < 0 || s.hex.col >= cfg.grid.cols) {
      throw new Error(`unit of team ${team} placed outside its half: ${s.hex.col},${s.hex.row}`)
    }
    // Stage 2 (additive): final stat percentages from tier scaling + passives (100 = unchanged)
    const pct = s.statPct ?? {}
    const scale = (v: number, p: number | undefined): number =>
      p !== undefined && p !== 100 ? Math.floor((v * p) / 100) : v
    const eff: ChampionConfig = pct === undefined ? c : {
      ...c,
      hp: scale(c.hp, pct.hp),
      ad: scale(c.ad, pct.ad),
      ap: scale(c.ap, pct.ap),
      armor: scale(c.armor, pct.armor),
      resist: scale(c.resist, pct.resist),
    }
    const extraAsPct = (pct.attackSpeed ?? 100) - 100
    const weapons = resolveWeapons(eff, s.weapons, cfg, extraAsPct, s.weaponTiers)
    let range = 0
    for (const w of weapons) range = Math.max(range, w.range)
    return {
      id, team, champ: s.champ, c: eff, weapons,
      maxHp: eff.hp, hp: eff.hp, shield: 0, mana: eff.manaStart,
      hex: { ...s.hex }, moveFrom: null, moveTicksTotal: 0, moveTicksLeft: 0,
      state: 'idle' as UnitState, targetId: -1, tauntedBy: -1, tauntTicksLeft: 0,
      dots: [], guaranteedCrit: false, attackRange: range,
      displayTier: Math.max(1, s.tier ?? 1),
    }
  })
}

/** helper (also defeats TS narrowing: applyDamage may kill `u` mid-block) */
function isDead(u: SimUnit): boolean {
  return u.state === 'dead'
}

function livingEnemies(st: SimState, u: SimUnit): SimUnit[] {
  return st.units.filter((e) => e.team !== u.team && e.state !== 'dead')
}

function livingAllies(st: SimState, u: SimUnit): SimUnit[] {
  return st.units.filter((e) => e.team === u.team && e.state !== 'dead')
}

/** nearest living enemy by hex distance; RNG tiebreak among equals (docs/03) */
function acquireTarget(st: SimState, u: SimUnit): number {
  const enemies = livingEnemies(st, u)
  if (enemies.length === 0) return -1
  let bestDist = Infinity
  let candidates: SimUnit[] = []
  for (const e of enemies) {
    const d = hexDistance(u.hex, e.hex)
    if (d < bestDist) {
      bestDist = d
      candidates = [e]
    } else if (d === bestDist) {
      candidates.push(e)
    }
  }
  const pick = candidates.length === 1 ? candidates[0]! : candidates[st.rng.nextInt(candidates.length)]!
  return pick.id
}

function applyDamage(
  st: SimState, source: SimUnit | null, target: SimUnit,
  raw: number, dtype: DamageType, crit: boolean, isDot: boolean,
): void {
  if (target.state === 'dead') return
  const K = st.cfg.combat.defenseK
  let dmg = raw
  if (dtype === 'phys') dmg = mitigate(raw, target.c.armor, K)
  else if (dtype === 'magic') dmg = mitigate(raw, target.c.resist, K)
  if (dmg < 0) dmg = 0
  // shields absorb first (docs/03)
  const absorbed = Math.min(target.shield, dmg)
  target.shield -= absorbed
  target.hp -= dmg - absorbed
  st.events.push({
    tick: st.tick, type: 'damage', unit: target.id,
    source: source ? source.id : -1, amount: dmg, dtype, crit, dot: isDot,
  })
  // mana on being hit — only direct hits, not DoT ticks (assumption A-CC2)
  if (!isDot && dmg > 0) {
    const gain = Math.floor((dmg + st.cfg.combat.manaPerDamageDivisor - 1) / st.cfg.combat.manaPerDamageDivisor)
    target.mana = Math.min(target.c.manaMax, target.mana + gain)
  }
  if (target.hp <= 0) {
    target.hp = 0
    target.state = 'dead'
    target.dots = []
    st.occ[hexIndex(target.hex, st.cols)] = -1
    st.events.push({ tick: st.tick, type: 'death', unit: target.id })
  }
}

function addDots(st: SimState, target: SimUnit, kind: DotKind, stacks: number): void {
  if (target.state === 'dead' || stacks <= 0) return
  const cb = st.cfg.combat
  const intervals = kind === 'poison' ? cb.poisonIntervals : cb.burnIntervals
  for (let i = 0; i < stacks; i++) {
    target.dots.push({ kind, intervalsLeft: intervals, ticksToNext: cb.dotIntervalTicks })
  }
  st.events.push({ tick: st.tick, type: 'dotApplied', unit: target.id, kind, stacks })
}

/** one weapon swing incl. crit roll, affinity, on-hit effects */
function fireWeapon(st: SimState, u: SimUnit, target: SimUnit, w: ResolvedWeapon): void {
  const cb = st.cfg.combat
  let raw = weaponBaseDamage(w.base, u.c.ad)
  if (w.damagePct > 0) raw = applyPctBonus(raw, w.damagePct)
  // crit: overflow above 100% chance converts to crit damage (Option B, docs/03)
  const critCfg = effectiveCrit(u.c.critChancePct, cb.critMultiplierPct)
  let critDmgPct = critCfg.damagePct
  let crit: boolean
  if (u.guaranteedCrit) {
    crit = true
    critDmgPct += st.cfg.abilities.shadowstrike.critBonusPct
    u.guaranteedCrit = false
  } else {
    crit = critCfg.chancePct > 0 && st.rng.rollPct(critCfg.chancePct)
  }
  if (crit) raw = Math.floor((raw * critDmgPct) / 100)
  st.events.push({ tick: st.tick, type: 'attack', unit: u.id, target: target.id, weapon: w.key })
  applyDamage(st, u, target, raw, 'phys', crit, false)
  // on-hit DoTs: Waffe (Gift-Dolch) + Champion-Pfad-Passive (Gift/Brennen-Applier)
  const poisonStacks = w.cfg.poisonOnHit + u.c.passivePoisonOnHit
  if (poisonStacks > 0) addDots(st, target, 'poison', poisonStacks)
  if (u.c.passiveBurnOnHit > 0) addDots(st, target, 'burn', u.c.passiveBurnOnHit)
  u.mana = Math.min(u.c.manaMax, u.mana + cb.manaPerAttack)
  w.cooldown = w.intervalTicks
}

function healUnit(st: SimState, source: SimUnit, target: SimUnit, amount: number): void {
  const healed = Math.min(target.maxHp - target.hp, amount) // Heilung nur bis Cap (docs/03)
  if (healed <= 0) return
  target.hp += healed
  st.events.push({ tick: st.tick, type: 'heal', unit: target.id, source: source.id, amount: healed })
}

function castAbility(st: SimState, u: SimUnit, target: SimUnit | null): void {
  const ab = st.cfg.abilities
  st.events.push({ tick: st.tick, type: 'ability', unit: u.id, name: u.c.ability })
  switch (u.c.ability) {
    case 'bulwark': {
      u.shield += ab.bulwark.shield
      st.events.push({ tick: st.tick, type: 'shield', unit: u.id, amount: ab.bulwark.shield })
      for (const e of livingEnemies(st, u)) {
        if (hexDistance(u.hex, e.hex) <= ab.bulwark.radius) {
          e.tauntedBy = u.id
          e.tauntTicksLeft = ab.bulwark.tauntTicks
          e.targetId = u.id
          st.events.push({ tick: st.tick, type: 'taunt', unit: e.id, source: u.id, untilTick: st.tick + ab.bulwark.tauntTicks })
        }
      }
      break
    }
    case 'shadowstrike': {
      u.guaranteedCrit = true
      break
    }
    case 'piercingShot': {
      if (!target) break
      const raw = Math.floor((u.c.ad * ab.piercingShot.adPct) / 100)
      // hits every living enemy standing in the target's grid row
      for (const e of livingEnemies(st, u)) {
        if (e.hex.row === target.hex.row) applyDamage(st, u, e, raw, 'phys', false, false)
      }
      break
    }
    case 'healingWave': {
      const amount = ab.healingWave.baseHeal + Math.floor((u.c.ap * ab.healingWave.apPct) / 100)
      const wounded = livingAllies(st, u)
        .filter((a) => a.hp < a.maxHp)
        .sort((x, y) => (y.maxHp - y.hp) - (x.maxHp - x.hp) || x.id - y.id)
        .slice(0, ab.healingWave.targets)
      for (const a of wounded) {
        const healed = Math.min(a.maxHp - a.hp, amount) // healing only up to cap (docs/03)
        a.hp += healed
        st.events.push({ tick: st.tick, type: 'heal', unit: a.id, source: u.id, amount: healed })
      }
      break
    }
    case 'plagueCloud': {
      if (!target) break
      for (const e of livingEnemies(st, u)) {
        if (hexDistance(target.hex, e.hex) <= ab.plagueCloud.radius) {
          addDots(st, e, 'poison', ab.plagueCloud.poisonStacks)
          addDots(st, e, 'burn', ab.plagueCloud.burnStacks)
        }
      }
      break
    }
    // ===== Content-Batch 1 (BATTLE-9, Engineering-Draft) =====
    case 'aegis': {
      // Paladin: schildet sich + die verwundetsten Verbuendeten (Schild ueber Cap erlaubt, docs/03)
      const amount = ab.aegis.shieldBase + Math.floor((u.c.ap * ab.aegis.apPct) / 100)
      const targets: SimUnit[] = [u]
      // Schild wirkt auch bei vollem Leben — nicht filtern, nur nach Verwundung sortieren
      const others = livingAllies(st, u)
        .filter((a) => a !== u)
        .sort((x, y) => (y.maxHp - y.hp) - (x.maxHp - x.hp) || x.id - y.id)
      targets.push(...others.slice(0, ab.aegis.extraTargets))
      for (const t of targets) {
        t.shield += amount
        st.events.push({ tick: st.tick, type: 'shield', unit: t.id, amount })
      }
      break
    }
    case 'fireball': {
      // Magier/Feuer: Magie-AoE um das Ziel + Brand
      if (!target) break
      const raw = Math.floor((u.c.ap * ab.fireball.apPct) / 100)
      for (const e of livingEnemies(st, u)) {
        if (hexDistance(target.hex, e.hex) <= ab.fireball.radius) {
          applyDamage(st, u, e, raw, 'magic', false, false)
          addDots(st, e, 'burn', ab.fireball.burnStacks)
        }
      }
      break
    }
    case 'chaosbolt': {
      // Hexenmeister/Zerstoerung: grosser Einzelziel-Magie-Burst
      if (!target) break
      const raw = Math.floor((u.c.ap * ab.chaosbolt.apPct) / 100)
      applyDamage(st, u, target, raw, 'magic', false, false)
      break
    }
    case 'moonfire': {
      // Druide/Gleichgewicht: Magieschaden + Brand-Stacks (Mondfeuer-DoT)
      if (!target) break
      const raw = Math.floor((u.c.ap * ab.moonfire.apPct) / 100)
      applyDamage(st, u, target, raw, 'magic', false, false)
      addDots(st, target, 'burn', ab.moonfire.burnStacks)
      break
    }
    case 'deathHarvest': {
      // Todesritter/Blut: Phys-AoE um sich selbst + Selbstheilung pro Treffer
      const raw = Math.floor((u.c.ad * ab.deathHarvest.adPct) / 100)
      let hits = 0
      for (const e of livingEnemies(st, u)) {
        if (hexDistance(u.hex, e.hex) <= ab.deathHarvest.radius) {
          applyDamage(st, u, e, raw, 'phys', false, false)
          hits++
        }
      }
      if (hits > 0) healUnit(st, u, u, ab.deathHarvest.healPerHit * hits)
      break
    }
    case 'flurry': {
      // Moench/Windlaeufer: Schlag-Serie aufs Ziel (Abilities critten nicht, A-CC7)
      if (!target) break
      const raw = Math.floor((u.c.ad * ab.flurry.adPct) / 100)
      for (let i = 0; i < ab.flurry.strikes; i++) {
        if (target.state === 'dead') break
        applyDamage(st, u, target, raw, 'phys', false, false)
      }
      break
    }
    case 'felNova': {
      // Daemonenjaeger/Verwuestung: Fel-Nova um sich selbst (magisch, AD-skaliert) + Brand
      const raw = Math.floor((u.c.ad * ab.felNova.adPct) / 100)
      for (const e of livingEnemies(st, u)) {
        if (hexDistance(u.hex, e.hex) <= ab.felNova.radius) {
          applyDamage(st, u, e, raw, 'magic', false, false)
          addDots(st, e, 'burn', ab.felNova.burnStacks)
        }
      }
      break
    }
    case 'chainLightning': {
      // Schamane/Elementar: Ziel + deterministischer Sprung zum naechsten Gegner beim Ziel
      if (!target) break
      const raw = Math.floor((u.c.ap * ab.chainLightning.apPct) / 100)
      applyDamage(st, u, target, raw, 'magic', false, false)
      let second: SimUnit | null = null
      let bestDist = ab.chainLightning.jumpRange + 1
      for (const e of livingEnemies(st, u)) {
        if (e.id === target.id) continue
        const d = hexDistance(target.hex, e.hex)
        if (d < bestDist || (d === bestDist && second && e.id < second.id)) {
          bestDist = d
          second = e
        }
      }
      if (second && bestDist <= ab.chainLightning.jumpRange) {
        const raw2 = Math.floor((u.c.ap * ab.chainLightning.secondaryPct) / 100)
        applyDamage(st, u, second, raw2, 'magic', false, false)
      }
      break
    }
    case 'boneSpear': {
      // Nekromant/Knochen: Magie-Linie durch die Ziel-Reihe (analog Durchschlag)
      if (!target) break
      const raw = Math.floor((u.c.ap * ab.boneSpear.apPct) / 100)
      for (const e of livingEnemies(st, u)) {
        if (e.hex.row === target.hex.row) applyDamage(st, u, e, raw, 'magic', false, false)
      }
      break
    }
    case 'bombToss': {
      // Tueftler/Sprengmeister: Sprengschaden-AoE um das Ziel (phys, docs/12 Keyword-Frage v0)
      if (!target) break
      const raw = Math.floor((u.c.ad * ab.bombToss.adPct) / 100)
      for (const e of livingEnemies(st, u)) {
        if (hexDistance(target.hex, e.hex) <= ab.bombToss.radius) {
          applyDamage(st, u, e, raw, 'phys', false, false)
        }
      }
      break
    }
  }
  u.mana = 0
  u.state = 'ability'
}

/** ability cast precondition — hold mana until the cast can do something */
function canCast(st: SimState, u: SimUnit, target: SimUnit | null): boolean {
  switch (u.c.ability) {
    case 'bulwark':
    case 'shadowstrike':
    case 'aegis':
      return true
    case 'piercingShot':
    case 'plagueCloud':
    case 'fireball':
    case 'chaosbolt':
    case 'moonfire':
    case 'flurry':
    case 'chainLightning':
    case 'boneSpear':
    case 'bombToss':
      return target !== null
    case 'healingWave':
      return livingAllies(st, u).some((a) => a.hp < a.maxHp)
    case 'deathHarvest': {
      const r = st.cfg.abilities.deathHarvest.radius
      return livingEnemies(st, u).some((e) => hexDistance(u.hex, e.hex) <= r)
    }
    case 'felNova': {
      const r = st.cfg.abilities.felNova.radius
      return livingEnemies(st, u).some((e) => hexDistance(u.hex, e.hex) <= r)
    }
  }
}

/** Assassin path passive: jump to a free hex next to the enemy backline at combat start */
function backlineJumps(st: SimState): void {
  for (const u of st.units) {
    if (u.state === 'dead' || !u.c.backlineJump) continue
    const half = st.cfg.grid.rowsPerSide
    // enemy rows ordered from their backline forwards
    const enemyRows: number[] = []
    if (u.team === 'A') {
      for (let r = 0; r < half; r++) enemyRows.push(r) // B backline = row 0
    } else {
      for (let r = st.rows - 1; r >= half; r--) enemyRows.push(r) // A backline = last row
    }
    const enemies = livingEnemies(st, u)
    if (enemies.length === 0) continue
    let dest: HexPos | null = null
    for (const row of enemyRows.slice(0, 2)) {
      let bestScore = Infinity
      let best: HexPos | null = null
      for (let col = 0; col < st.cols; col++) {
        const p = { col, row }
        if (st.occ[hexIndex(p, st.cols)] !== -1) continue
        // nearest enemy from candidate hex; tiebreak: lower col (deterministic, no RNG needed)
        let d = Infinity
        for (const e of enemies) d = Math.min(d, hexDistance(p, e.hex))
        const score = d * 100 + col
        if (score < bestScore) {
          bestScore = score
          best = p
        }
      }
      if (best) {
        dest = best
        break
      }
    }
    if (dest) {
      const from = { ...u.hex }
      st.occ[hexIndex(u.hex, st.cols)] = -1
      u.hex = dest
      st.occ[hexIndex(dest, st.cols)] = u.id
      st.events.push({ tick: 0, type: 'jump', unit: u.id, from, to: dest })
    }
  }
}

function snapshotUnits(st: SimState): UnitSnapshot[] {
  return st.units.map((u) => {
    let poison = 0
    let burn = 0
    for (const d of u.dots) {
      if (d.kind === 'poison') poison++
      else burn++
    }
    const from = u.moveFrom ?? u.hex
    const movePct = u.moveFrom && u.moveTicksTotal > 0
      ? Math.floor(((u.moveTicksTotal - u.moveTicksLeft) * 100) / u.moveTicksTotal)
      : 100
    return {
      id: u.id, col: u.hex.col, row: u.hex.row,
      fromCol: from.col, fromRow: from.row, movePct,
      hp: u.hp, shield: u.shield, mana: u.mana, state: u.state,
      poisonStacks: poison, burnStacks: burn, taunted: u.tauntTicksLeft > 0,
    }
  })
}

function teamScore(st: SimState, team: TeamId, startUnits: number, startHp: number) {
  let unitsLeft = 0
  let hpLeft = 0
  for (const u of st.units) {
    if (u.team === team && u.state !== 'dead') {
      unitsLeft++
      hpLeft += u.hp
    }
  }
  const p = st.cfg.power
  const powerBp =
    Math.floor((p.weightUnitsPct * 100 * unitsLeft) / startUnits) +
    Math.floor((p.weightHpPct * 100 * hpLeft) / startHp)
  return { unitsLeft, unitsStart: startUnits, hpLeft, hpStart: startHp, powerBp }
}

export function simulate(
  seed: number,
  setupA: TeamSetup,
  setupB: TeamSetup,
  config: BalanceConfig,
): CombatResult {
  const cfg = config
  const cols = cfg.grid.cols
  const rows = cfg.grid.rowsPerSide * 2
  const units = buildUnits(setupA, setupB, cfg)
  const st: SimState = {
    cfg, rng: createRng(seed), cols, rows, units,
    occ: new Int32Array(cols * rows).fill(-1),
    events: [], tick: 0,
  }
  for (const u of units) {
    const idx = hexIndex(u.hex, cols)
    if (st.occ[idx] !== -1) throw new Error(`two units on hex ${u.hex.col},${u.hex.row}`)
    st.occ[idx] = u.id
    st.events.push({ tick: 0, type: 'spawn', unit: u.id, hex: { ...u.hex } })
  }
  backlineJumps(st)

  const startUnitsA = setupA.length
  const startUnitsB = setupB.length
  let startHpA = 0
  let startHpB = 0
  for (const u of units) {
    if (u.team === 'A') startHpA += u.maxHp
    else startHpB += u.maxHp
  }

  const snapshots: UnitSnapshot[][] = [snapshotUnits(st)]
  const cb = cfg.combat
  let winner: TeamId | 'draw' = 'draw'
  let reason: EndReason = 'cutoff'

  simLoop:
  for (let tick = 1; tick <= cb.hardCutoffTick; tick++) {
    st.tick = tick
    // Overtime acceleration: all timers advance x2 from overtimeStartTick (docs/03)
    const accel = tick >= cb.overtimeStartTick ? cb.overtimeMultiplier : 1

    for (const u of st.units) {
      if (u.state === 'dead') continue

      // 1) status update: cooldowns, taunt, DoTs
      for (const w of u.weapons) w.cooldown = Math.max(0, w.cooldown - accel)
      if (u.tauntTicksLeft > 0) {
        u.tauntTicksLeft = Math.max(0, u.tauntTicksLeft - accel)
        if (u.tauntTicksLeft === 0) u.tauntedBy = -1
      }
      if (u.dots.length > 0) {
        for (const d of u.dots) {
          d.ticksToNext -= accel
          while (d.ticksToNext <= 0 && d.intervalsLeft > 0 && !isDead(u)) {
            const dmg = d.kind === 'poison'
              ? cb.poisonFlat
              : Math.floor((u.maxHp * cb.burnPctMaxHp) / 100)
            applyDamage(st, null, u, dmg, 'true', false, true)
            d.intervalsLeft--
            d.ticksToNext += cb.dotIntervalTicks
          }
        }
        u.dots = u.dots.filter((d) => d.intervalsLeft > 0)
        if (isDead(u)) continue // died to own DoT ticks
      }

      // 2) target acquisition (taunt overrides, docs/03)
      const taunter = u.tauntedBy >= 0 ? st.units[u.tauntedBy]! : null
      if (u.tauntTicksLeft > 0 && taunter && taunter.state !== 'dead') {
        u.targetId = taunter.id
      } else {
        const cur = u.targetId >= 0 ? st.units[u.targetId]! : null
        if (!cur || cur.state === 'dead') u.targetId = acquireTarget(st, u)
      }
      if (u.targetId === -1) {
        u.state = 'idle'
        continue
      }
      let target = st.units[u.targetId]!

      // 3) range check (transit must be finished before acting)
      const inTransit = u.moveTicksLeft > 0
      const dist = hexDistance(u.hex, target.hex)
      const inRange = dist <= u.attackRange && !inTransit

      // 4) action: ability > attack > move
      if (!inTransit && u.mana >= u.c.manaMax && canCast(st, u, target)) {
        castAbility(st, u, target)
        continue
      }
      if (inRange) {
        u.state = 'attacking'
        for (const w of u.weapons) {
          if (target.state === 'dead') break
          if (w.cooldown <= 0 && dist <= w.range) fireWeapon(st, u, target, w)
        }
        continue
      }
      // moving
      u.state = 'moving'
      if (inTransit) {
        u.moveTicksLeft = Math.max(0, u.moveTicksLeft - accel)
        if (u.moveTicksLeft === 0) u.moveFrom = null
        continue
      }
      const blocked = (idx: number): boolean => st.occ[idx] !== -1 && st.occ[idx] !== u.id
      let path = findPath(u.hex, target.hex, u.attackRange, cols, rows, blocked)
      if (path === null) {
        // Option B: switch target only if NO path to the current one exists
        const others = livingEnemies(st, u)
          .filter((e) => e.id !== target.id)
          .sort((a, b) => hexDistance(u.hex, a.hex) - hexDistance(u.hex, b.hex) || a.id - b.id)
        for (const e of others) {
          const p = findPath(u.hex, e.hex, u.attackRange, cols, rows, blocked)
          if (p !== null) {
            u.targetId = e.id
            target = e
            path = p
            break
          }
        }
      }
      if (path === null || path.length === 0) {
        u.state = 'idle' // boxed in — retry next tick
        continue
      }
      const next = path[0]!
      const fromHex = { ...u.hex }
      st.occ[hexIndex(u.hex, cols)] = -1
      st.occ[hexIndex(next, cols)] = u.id
      u.moveFrom = fromHex
      u.hex = { ...next }
      u.moveTicksTotal = u.c.moveTicksPerHex
      u.moveTicksLeft = Math.max(0, u.c.moveTicksPerHex - accel)
      if (u.moveTicksLeft === 0) u.moveFrom = null
      st.events.push({
        tick, type: 'moveStart', unit: u.id,
        from: fromHex, to: { ...next }, duration: u.moveTicksTotal,
      })
    }

    snapshots.push(snapshotUnits(st))

    // end conditions
    let aliveA = 0
    let aliveB = 0
    for (const u of st.units) {
      if (u.state === 'dead') continue
      if (u.team === 'A') aliveA++
      else aliveB++
    }
    if (aliveA === 0 || aliveB === 0) {
      if (aliveA === 0 && aliveB === 0) {
        winner = 'draw'
        reason = 'mutual'
      } else {
        winner = aliveA > 0 ? 'A' : 'B'
        reason = 'elimination'
      }
      break simLoop
    }
    if (tick === cb.hardCutoffTick) {
      // hard cutoff scoring: more units, tiebreak total remaining HP (docs/03)
      let hpA = 0
      let hpB = 0
      for (const u of st.units) {
        if (u.state === 'dead') continue
        if (u.team === 'A') hpA += u.hp
        else hpB += u.hp
      }
      reason = 'cutoff'
      if (aliveA !== aliveB) winner = aliveA > aliveB ? 'A' : 'B'
      else if (hpA !== hpB) winner = hpA > hpB ? 'A' : 'B'
      else winner = 'draw'
    }
  }

  const endTick = snapshots.length - 1
  st.tick = endTick
  st.events.push({ tick: endTick, type: 'end', winner, reason })

  const metas: UnitMeta[] = units.map((u) => ({
    id: u.id, team: u.team, champ: u.champ, name: u.c.name, role: u.c.role,
    maxHp: u.maxHp, manaMax: u.c.manaMax, weapons: u.weapons.map((w) => w.key),
    tier: u.displayTier,
  }))

  return {
    winner, reason, endTick,
    scoreA: teamScore(st, 'A', startUnitsA, startHpA),
    scoreB: teamScore(st, 'B', startUnitsB, startHpB),
    units: metas,
    events: st.events,
    snapshots,
  }
}
